/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { Button, makeStyles } from '@material-ui/core';
import React from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import Icons from './assets/Icons';
import { getFeatureFlags } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { useSelector } from 'react-redux';
import { bool, func, string } from 'prop-types';
import { FacebookShareButton, TwitterShareButton, EmailShareButton } from 'react-share';
import { bindActionCreators } from 'redux';
import { trackEvent as track } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { isMobile } from 'react-device-detect';

const useStyles = (mobile, isDadJokeGPT) =>
    makeStyles((theme) => ({
        socialWrapper: {
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: mobile ? '16px' : '34px',
            marginBottom: '24px',
        },
        socialContainer: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            '& > span': {
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '18px',
            },
        },
        icon: {
            maxHeight: '29px',
        },
        socialIcon: {
            position: 'relative !important',
            width: `${mobile ? '54px' : '64px'} !important`,
            height: mobile ? '54px' : '64px',
            borderRadius: '50% !important',
            margin: '0 0 12px 0 !important',
            backgroundColor: `${isDadJokeGPT ? '#081E3F' : theme.palette.common.black}!important`,
            fontWeight: '700 !important',
            minWidth: 'unset !important',
            border: '0 !important',
        },
        individualShareButton: {
            width: '127px',
            height: '40px',
            backgroundColor: `${isDadJokeGPT ? '#081E3F' : '#F55858'} !important`,
            ...(!isDadJokeGPT ? { border: '0 !important' } : {}),
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '14px',
            letterSpacing: '0.04em',
            lineHeight: '20px',
            borderRadius: '3px !important',
            color: `${theme.palette.common.white}`,
            margin: '0 auto 16px auto',
            marginRight: 'auto !important',
            '& svg': {
                maxWidth: '16px',
                marginLeft: '6px',
            },
            '&:disabled': {
                border: '0',
                opacity: '0.08',
                color: `${theme.palette.common.white}`,
                width: '103px !important',
                borderRadius: '30px !important',
            },
        },
    }));

const SocialMedia = ({ isDadJokeGPT, trackEvent, ...props }) => {
    const classesLayout = useStyles(props.mobile, isDadJokeGPT);
    const classes = classesLayout();
    const url = window?.location?.href || 'www.1800flowers.com/account/momverse';

    const featureFlags = useSelector(getFeatureFlags);

    const activeSocialMedia = featureFlags['which-poem-page-social-media-share-enabled'];
    const isAllSocialsDisabled = Object.values(activeSocialMedia).every((item) => item === false);

    const { facebook, twitter, email } = activeSocialMedia;

    const handleMobileSocialClick = async () => {
        trackEvent({
            eventCategory: isDadJokeGPT ? 'DadJokeGPT' : 'Momverse',
            eventAction: 'Share',
            eventLabel: 'Mobile',
        });
        try {
            const dataUrl = await props.getDataUrl();
            const blob = await (await fetch(dataUrl)).blob();
            const file = new File([blob], 'poem.png', { type: blob.type });
            navigator.share({
                title: '',
                files: [file],
            });
        } catch (error) {
            console.error(error);
        }
    };

    if (isMobile && navigator && navigator.share)
        return (
            <Button
                onClick={handleMobileSocialClick}
                className={classes.individualShareButton}
                type="button"
                tabIndex="0"
            >
                Share <Icons icon="share" />
            </Button>
        );

    const ctaTracking = (platform = '') => {
        trackEvent({
            eventCategory: isDadJokeGPT ? 'DadJokeGPT' : 'Momverse',
            eventAction: 'Share',
            eventLabel: platform,
        });
    };

    if (!isAllSocialsDisabled && !isMobile)
        return (
            <div className={classes.socialWrapper}>
                {facebook && (
                    <div className={classes.socialContainer} onClick={() => ctaTracking('Facebook')}>
                        <FacebookShareButton
                            url={url}
                            hashtag={isDadJokeGPT ? 'DadJokeGPT' : 'Momverse'}
                            aria-labelledby="facebookLabel"
                            className={`${classes.socialIcon} roundButton`}
                        >
                            <Icons className={classes.icon} icon="facebook" />
                        </FacebookShareButton>
                        <span id="facebookLabel">Facebook</span>
                    </div>
                )}
                {twitter && (
                    <div className={classes.socialContainer} onClick={() => ctaTracking('Twitter')}>
                        <TwitterShareButton
                            url={url}
                            title={props.poem}
                            aria-labelledby="twitterLabel"
                            className={`${classes.socialIcon} roundButton`}
                        >
                            <Icons className={classes.icon} icon="twitter" />
                        </TwitterShareButton>
                        <span id="twitterLabel">Twitter</span>
                    </div>
                )}
                {email && (
                    <div className={classes.socialContainer} onClick={() => ctaTracking('Email')}>
                        <EmailShareButton
                            url={url}
                            subject={isDadJokeGPT ? 'DadJokeGPT' : 'Momverse'}
                            body={props.poem}
                            separator={' - '}
                            aria-labelledby="emailLabel"
                            className={`${classes.socialIcon} roundButton`}
                        >
                            <Icons className={classes.icon} icon="email" />
                        </EmailShareButton>
                        <span id="textLabel">Email</span>
                    </div>
                )}
            </div>
        );

    return null;
};

SocialMedia.propTypes = {
    mobile: bool.isRequired,
    getDataUrl: func.isRequired,
    poem: string.isRequired,
    isDadJokeGPT: bool.isRequired,
    trackEvent: func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(track, dispatch),
});

const enhance = compose(connect(null, mapDispatchToProps));

export default enhance(SocialMedia);
